import { useCallback, useEffect, useRef, useState } from "react";
import { StatusMessage } from "../ui";
import { FormErrorSummary } from "../ui/FormErrorSummary";
import { OptionsSelectInput } from "../inputs/OptionsSelectInput";
import { getSitesByCustomerGrid } from "../../services/csh/csh.service";

type AssignedToValue =
    | string
    | {
          id: string | number;
          label?: string;
      };

type User = {
    id: string | number;
    firstName: string;
    lastName: string;
    employeeId: string;
};

type Customer = {
    id: string | number;
    customerName: string;
};

type Site = {
    id: string | number;
    displayName?: string;
    siteName?: string;
};

type InitialSiteOption = {
    id: string | number;
    displayName?: string;
    siteName?: string;
};

type RawSite = {
    id?: string | number;
    siteId?: string | number;
    siteIdentifierRegistryId?: string | number;
    siteGuid?: string | number;
    siteIdentifier?: string;
    displayName?: string;
    siteName?: string;
};

type AssignedToItem = {
    id: string;
    label: string;
};

type SelectedAssignedTo = AssignedToValue[] | AssignedToValue | null;

export type CustomerEntry = {
    customerId: string;
    selectedSites: string[];
};

export type CSHFormData = {
    name: string;
    customers: CustomerEntry[];
    assignedTo: AssignedToItem[];
};

type SelectedCustomerData = {
    customerId: string;
    selectedSites: string[];
    siteOptions?: InitialSiteOption[];
};

type CSHFormErrors = Partial<Record<"name" | "assignedTo" | "customers", string>>;

function toAssignedToArray(
    selectedAssignedTo: SelectedAssignedTo
): AssignedToValue[] {
    if (!selectedAssignedTo) {
        return [];
    }

    if (Array.isArray(selectedAssignedTo)) {
        return selectedAssignedTo;
    }

    return [selectedAssignedTo];
}

function getAssignedToId(value: AssignedToValue): string | null {
    let rawId: string | number | undefined;

    if (typeof value === "object" && value !== null) {
        rawId = value.id;
    } else {
        rawId = value;
    }

    if (!rawId) {
        return null;
    }

    return String(rawId);
}

function getAssignedToLabel(
    value: AssignedToValue,
    id: string,
    usersData: User[]
): string {
    const user = usersData.find(u => String(u.id) === id);
    if (user) {
        const name = `${user.firstName} ${user.lastName}`;
        return user.employeeId ? `${name} - ${user.employeeId}` : name;
    }

    if (typeof value === "object" && value?.label) {
        return value.label;
    }

    return id;
}

function normalizeAssignedTo(
    selectedAssignedTo: SelectedAssignedTo,
    usersData: User[]
): AssignedToItem[] {
    const assignedToValues = toAssignedToArray(selectedAssignedTo);

    return assignedToValues
        .map(s => {
            const id = getAssignedToId(s);
            if (!id) {
                return null;
            }

            const label = getAssignedToLabel(s, id, usersData);

            return { id, label };
        })
        .filter((item): item is AssignedToItem => item !== null);
}

async function fetchSitesData(customerId: string): Promise<Site[]> {
    const customerIdNum = Number(customerId);
    if (!customerIdNum) {
        return [];
    }

    const response = await getSitesByCustomerGrid(customerIdNum);
    return (response.data ?? []).reduce<Site[]>((acc, site) => {
        const rawSite = site as RawSite;
        const siteId = rawSite.siteIdentifierRegistryId
            ?? rawSite.siteId
            ?? rawSite.id
            ?? rawSite.siteGuid;

        if (siteId === undefined || siteId === null) {
            return acc;
        }

        const normalizedSite = {
            id: String(siteId),
            displayName: rawSite.siteIdentifier ?? rawSite.displayName ?? rawSite.siteName ?? String(siteId),
            siteName: rawSite.siteName,
        };

        const exists = acc.some(existingSite => String(existingSite.id) === normalizedSite.id);
        if (!exists) {
            acc.push(normalizedSite);
        }

        return acc;
    }, []);
}

function shouldInitializeFormData(
    mode: string,
    selectedCustomers: SelectedCustomerData[] | null,
    initRef: { current: boolean }
): boolean {
    return mode === "edit"
        && Array.isArray(selectedCustomers)
        && selectedCustomers.length > 0
        && !initRef.current;
}

function normalizeSelectedSites(selectedSites: string[] | null): string[] {
    if (!Array.isArray(selectedSites)) {
        return [];
    }

    return selectedSites.map(String);
}

function normalizeInitialSiteOptions(selectedSiteOptions: InitialSiteOption[] | null): Site[] {
    if (!Array.isArray(selectedSiteOptions)) {
        return [];
    }

    return selectedSiteOptions.reduce<Site[]>((acc, site) => {
        if (site.id === undefined || site.id === null) {
            return acc;
        }

        acc.push({
            id: String(site.id),
            displayName: site.displayName ?? site.siteName ?? String(site.id),
            siteName: site.siteName,
        });

        return acc;
    }, []);
}

function getFormTitle(mode: string): string {
    if (mode === "edit") {
        return "Edit CSH Group";
    }

    return "Add CSH Group";
}

function renderServerErrorMessage(
    serverError: string | null,
    onClearServerError: () => void
): React.ReactNode {
    if (!serverError) {
        return null;
    }

    return (
        <StatusMessage
            key={serverError}
            kind="error"
            onClose={onClearServerError}
        >
            {serverError}
        </StatusMessage>
    );
}

function renderErrorSummaryMessage(
    showErrorSummary: boolean,
    errors: CSHFormErrors,
    handleCloseStatusMessage: () => void
): React.ReactNode {
    const errorMessages = Object.values(errors).filter(Boolean);
    if (!showErrorSummary || !errors || errorMessages.length === 0) {
        return null;
    }

    return (
        <StatusMessage
            kind="error"
            onClose={handleCloseStatusMessage}
        >
            <FormErrorSummary
                errors={errorMessages}
            />
        </StatusMessage>
    );
}

export const CSHForm = ({
    mode,
    formData,
    setFormData,
    usersData,
    customerData,
    errors,
    validateField,
    hasSubmitted,
    serverError,
    onClearServerError,
    selectedCustomers,
    selectedAssignedTo,
}: {
    mode: string;
    formData: CSHFormData;
    setFormData: (data: CSHFormData | ((prev: CSHFormData) => CSHFormData)) => void;
    usersData: User[];
    customerData: Customer[];
    errors: CSHFormErrors;
    validateField: (data: CSHFormData) => void;
    hasSubmitted: boolean;
    serverError: string | null;
    onClearServerError: () => void;
    selectedCustomers: SelectedCustomerData[] | null;
    selectedAssignedTo: SelectedAssignedTo;
}) => {
    const [showErrorSummary, setShowErrorSummary] = useState(true);
    const [sitesMap, setSitesMap] = useState<Record<string, Site[]>>({});
    const [loadingSitesMap, setLoadingSitesMap] = useState<Record<string, boolean>>({});
    const [customerSearchTerm, setCustomerSearchTerm] = useState("");
    const [siteSearchTerms, setSiteSearchTerms] = useState<Record<string, string>>({});
    const [customerSearchExpanded, setCustomerSearchExpanded] = useState(false);
    const [siteSearchExpandedMap, setSiteSearchExpandedMap] = useState<Record<string, boolean>>({});
    const initRef = useRef(false);
    const fetchingRef = useRef<Set<string>>(new Set());
    const customersSectionRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLDivElement>(null);
    const prevErrorCountRef = useRef(0);
    const customerSearchInputRef = useRef<HTMLInputElement>(null);
    const siteSearchInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    const handleCloseStatusMessage = useCallback(() => {
        setShowErrorSummary(false);
    }, []);

    const handleCustomerSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomerSearchTerm(e.target.value);
    }, []);

    const handleSiteSearchChange = useCallback((customerId: string, e: React.ChangeEvent<HTMLInputElement>) => {
        setSiteSearchTerms(prev => ({
            ...prev,
            [customerId]: e.target.value,
        }));
    }, []);

    const handleClearCustomerSearch = useCallback(() => {
        setCustomerSearchTerm("");
        setCustomerSearchExpanded(false);
    }, []);

    const handleClearSiteSearch = useCallback((customerId: string) => {
        setSiteSearchTerms(prev => ({
            ...prev,
            [customerId]: "",
        }));
        setSiteSearchExpandedMap(prev => ({
            ...prev,
            [customerId]: false,
        }));
    }, []);

    const handleExpandCustomerSearch = useCallback(() => {
        setCustomerSearchExpanded(true);
        setTimeout(() => {
            customerSearchInputRef.current?.focus();
        }, 0);
    }, []);

    const handleExpandSiteSearch = useCallback((customerId: string) => {
        setSiteSearchExpandedMap(prev => ({
            ...prev,
            [customerId]: true,
        }));
        setTimeout(() => {
            siteSearchInputRefs.current[customerId]?.focus();
        }, 0);
    }, []);

    const handleCollapseCustomerSearch = useCallback(() => {
        if (!customerSearchTerm) {
            setCustomerSearchExpanded(false);
        }
    }, [customerSearchTerm]);

    const handleCollapseSiteSearch = useCallback((customerId: string) => {
        if (!siteSearchTerms[customerId]) {
            setSiteSearchExpandedMap(prev => ({
                ...prev,
                [customerId]: false,
            }));
        }
    }, [siteSearchTerms]);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const updated = {
            ...formData,
            name: e.target.value,
        };
        setFormData(updated);
        if (hasSubmitted) {
            validateField(updated);
        }
    }, [formData, hasSubmitted, validateField]);

    const handleAssignedToChange = useCallback((values: (string | number)[]) => {
        const updatedAssignedTo = values
            .map(rawId => {
                const id = String(rawId);
                const user = usersData.find(u => String(u.id) === id);
                if (!user) {
                    return null;
                }

                const name = `${user.firstName} ${user.lastName}`;
                return { id: String(user.id), label: user.employeeId ? `${name} - ${user.employeeId}` : name };
            })
            .filter((item): item is AssignedToItem => item !== null);

        const updated = {
            ...formData,
            assignedTo: updatedAssignedTo,
        };

        setFormData(updated);
        if (hasSubmitted) {
            validateField(updated);
        }
    }, [formData, hasSubmitted, validateField, usersData]);

    const handleToggleCustomer = useCallback((customerId: string) => {
        const exists = formData.customers.some(c => c.customerId === customerId);
        let newCustomers: CustomerEntry[];

        if (exists) {
            newCustomers = formData.customers.filter(c => c.customerId !== customerId);
        } else {
            newCustomers = [...formData.customers, { customerId, selectedSites: [] }];
        }

        const updated = { ...formData, customers: newCustomers };
        setFormData(updated);
        if (hasSubmitted) {
            validateField(updated);
        }

        if (!exists) {
            requestAnimationFrame(() => {
                const section = customersSectionRef.current;
                if (section) {
                    const lastChild = section.lastElementChild;
                    lastChild?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }
            });
        }
    }, [formData, hasSubmitted, validateField, setFormData]);

    const handleToggleAllCustomers = useCallback(() => {
        const filteredCustomers = customerData
            .filter(c => c.customerName.toLowerCase().includes(customerSearchTerm.toLowerCase()));
        
        const allSelected = filteredCustomers.length > 0 && 
            filteredCustomers.every(c => formData.customers.some(fc => fc.customerId === String(c.id)));
        
        let newCustomers: CustomerEntry[];
        
        if (allSelected) {
            // Deselect all filtered customers
            const filteredCustomerIds = filteredCustomers.map(c => String(c.id));
            newCustomers = formData.customers.filter(c => !filteredCustomerIds.includes(c.customerId));
        } else {
            // Select all filtered customers
            const filteredCustomerIds = filteredCustomers.map(c => String(c.id));
            const existingIds = new Set(formData.customers.map(c => c.customerId));
            const toAdd = filteredCustomerIds
                .filter(id => !existingIds.has(id))
                .map(customerId => ({ customerId, selectedSites: [] }));
            newCustomers = [...formData.customers, ...toAdd];
        }

        const updated = { ...formData, customers: newCustomers };
        setFormData(updated);
        if (hasSubmitted) {
            validateField(updated);
        }
    }, [formData, hasSubmitted, validateField, setFormData, customerData, customerSearchTerm]);

    const handleToggleSite = useCallback((customerId: string, siteId: string) => {
        const newCustomers = formData.customers.map(c => {
            if (c.customerId !== customerId) return c;
            const has = c.selectedSites.includes(siteId);
            return {
                ...c,
                selectedSites: has
                    ? c.selectedSites.filter(s => s !== siteId)
                    : [...c.selectedSites, siteId],
            };
        });
        const updated = { ...formData, customers: newCustomers };
        setFormData(updated);
        if (hasSubmitted) {
            validateField(updated);
        }
    }, [formData, hasSubmitted, validateField, setFormData]);

    const handleToggleAllSites = useCallback((customerId: string, sites: Site[]) => {
        const newCustomers = formData.customers.map(c => {
            if (c.customerId !== customerId) return c;
            const allSiteIds = sites.map(s => String(s.id));
            const allSelected = allSiteIds.length > 0 && allSiteIds.every(id => c.selectedSites.includes(id));
            return {
                ...c,
                selectedSites: allSelected ? [] : allSiteIds,
            };
        });
        const updated = { ...formData, customers: newCustomers };
        setFormData(updated);
        if (hasSubmitted) {
            validateField(updated);
        }
    }, [formData, hasSubmitted, validateField, setFormData]);

    useEffect(() => {
        if (!shouldInitializeFormData(mode, selectedCustomers, initRef)) {
            return;
        }

        const customers: CustomerEntry[] = (selectedCustomers ?? [])
            .filter(sc => customerData?.some(c => String(c.id) === sc.customerId))
            .map(sc => ({
                customerId: sc.customerId,
                selectedSites: normalizeSelectedSites(sc.selectedSites ?? null),
            }));

        if (customers.length === 0) {
            initRef.current = true;
            return;
        }

        setFormData(prev => ({
            ...prev,
            customers,
            assignedTo: normalizeAssignedTo(selectedAssignedTo, usersData),
        }));

        const seededSites: Record<string, Site[]> = {};
        (selectedCustomers ?? []).forEach(sc => {
            if (!sc.siteOptions) return;
            const seeded = normalizeInitialSiteOptions(sc.siteOptions);
            if (seeded.length > 0) {
                seededSites[sc.customerId] = seeded;
            }
        });
        if (Object.keys(seededSites).length > 0) {
            setSitesMap(prev => ({ ...prev, ...seededSites }));
        }

        initRef.current = true;
    }, [
        mode,
        selectedCustomers,
        selectedAssignedTo,
        setFormData,
        customerData,
        usersData,
    ]);

    // Reset the init.
    useEffect(() => {
        if (mode !== "edit") {
            initRef.current = false;
        }
    }, [mode]);

    useEffect(() => {
        const errorCount = errors ? Object.values(errors).filter(Boolean).length : 0;
        if (errorCount > 0) {
            setShowErrorSummary(true);
            if (prevErrorCountRef.current === 0) {
                formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
        prevErrorCountRef.current = errorCount;
    }, [errors]);

    const customerIdsKey = formData.customers.map(c => c.customerId).filter(Boolean).join(",");

    useEffect(() => {
        const entries = formData.customers.filter(
            c => c.customerId && !sitesMap[c.customerId] && !fetchingRef.current.has(c.customerId)
        );
        if (entries.length === 0) {
            return;
        }

        entries.forEach(e => fetchingRef.current.add(e.customerId));

        // Mark all pending customers as loading upfront
        setLoadingSitesMap(prev => {
            const next = { ...prev };
            entries.forEach(e => { next[e.customerId] = true; });
            return next;
        });

        const loadAllSites = async () => {
            for (const entry of entries) {
                try {
                    const loaded = await fetchSitesData(entry.customerId);
                    setSitesMap(prev => ({ ...prev, [entry.customerId]: loaded }));
                } catch {
                    setSitesMap(prev => ({ ...prev, [entry.customerId]: [] }));
                } finally {
                    fetchingRef.current.delete(entry.customerId);
                    setLoadingSitesMap(prev => ({ ...prev, [entry.customerId]: false }));
                }
            }
        };

        loadAllSites();
    }, [customerIdsKey]);

    return (
        <div className="form form--csh" ref={formRef}>
            {renderServerErrorMessage(serverError, onClearServerError)}

            {renderErrorSummaryMessage(showErrorSummary, errors, handleCloseStatusMessage)}

            <div className="form__group">
                <label htmlFor="name" className="form__label">Name</label>
                <input
                    id="name"
                    className={`form__control ${errors?.name && "form__control--error"}`}
                    value={formData.name}
                    onChange={handleNameChange}
                    autoComplete="off"
                />
            </div>

            <div className="form__group">
                <label htmlFor="assignedTo" className="form__label">Assigned To</label>
                <OptionsSelectInput
                    options={usersData
                        .map(u => ({
                            id: String(u.id),
                            label: u.employeeId ? `${u.firstName} ${u.lastName} - ${u.employeeId}` : `${u.firstName} ${u.lastName}`,
                        }))}
                    selected={formData.assignedTo.map(u => u.id)}
                    onChange={handleAssignedToChange}
                    placeholder="Select user(s)..."
                    hasError={!!errors?.assignedTo}
                    showChipLabelTooltip
                />
            </div>

            <div className="form__customers-section" ref={customersSectionRef}>
                <div className="form__customers-header">
                    <span className="form__label">
                        Customers
                        {formData.customers.filter(c => c.customerId).length > 0 && (
                            <span className="form__customers-count">
                                ({formData.customers.filter(c => c.customerId).length} / {customerData.length})
                            </span>
                        )}
                    </span>
                    
                    <div className="form__customers-controls">
                        {customerData.length > 4 && (
                            <div className={`form__search-expandable ${customerSearchExpanded ? 'form__search-expandable--expanded' : ''}`}>
                                <button
                                    type="button"
                                    className="form__search-icon-btn"
                                    onClick={handleExpandCustomerSearch}
                                    aria-label="Open customer search"
                                >
                                    <span className="se-icon se-icon--search" />
                                </button>
                                <div className="form__search-input-wrapper">
                                    <input
                                        ref={customerSearchInputRef}
                                        type="text"
                                        className="form__control form__control--search"
                                        placeholder="Search customers..."
                                        value={customerSearchTerm}
                                        onChange={handleCustomerSearchChange}
                                        onBlur={handleCollapseCustomerSearch}
                                        autoComplete="off"
                                    />
                                    {customerSearchTerm && (
                                        <button
                                            type="button"
                                            className="form__search-clear"
                                            onClick={handleClearCustomerSearch}
                                            aria-label="Clear customer search"
                                        >
                                            <span className="se-icon se-icon--close" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {customerData.length > 1 && (
                            <label className="csh-select-all-label">
                                <input
                                    type="checkbox"
                                    className="csh-select-all-checkbox"
                                    checked={customerData.length > 0 && customerData
                                        .filter(c => c.customerName.toLowerCase().includes(customerSearchTerm.toLowerCase()))
                                        .every(c => formData.customers.some(fc => fc.customerId === String(c.id)))}
                                    onChange={handleToggleAllCustomers}
                                    aria-label="Select all customers"
                                />
                                <span className="csh-select-all-text">Select All</span>
                            </label>
                        )}
                    </div>
                </div>

                <div className="csh-bubble-list">
                    {customerData
                        .toSorted((a, b) => a.customerName.localeCompare(b.customerName))
                        .filter(c => c.customerName.toLowerCase().includes(customerSearchTerm.toLowerCase()))
                        .map(c => {
                            const cId = String(c.id);
                            const isSelected = formData.customers.some(fc => fc.customerId === cId);
                            return (
                                <button
                                    key={cId}
                                    type="button"
                                    className={`csh-bubble ${isSelected ? "csh-bubble--selected" : ""}`}
                                    onClick={() => handleToggleCustomer(cId)}
                                >
                                    {c.customerName}
                                </button>
                            );
                        })}
                </div>

                {customerSearchTerm && customerData.filter(c => c.customerName.toLowerCase().includes(customerSearchTerm.toLowerCase())).length === 0 && (
                    <div className="csh-no-results">
                        <span className="csh-no-results__text">No matching customers found</span>
                    </div>
                )}

                {formData.customers.filter(c => c.customerId).length > 0 && (
                    <div className="form__sites-header">
                        <span className="form__label">Sites</span>
                    </div>
                )}

                <div className="csh-sites-container">
                    {formData.customers.filter(c => c.customerId).map(entry => {
                        const customer = customerData.find(c => String(c.id) === entry.customerId);
                        if (!customer) return null;
                        const sitesLoaded = entry.customerId in sitesMap;
                        const customerSites = sitesMap[entry.customerId] ?? [];
                        const isLoading = !sitesLoaded || (loadingSitesMap[entry.customerId] ?? false);
                        const siteSearchTerm = siteSearchTerms[entry.customerId] ?? "";
                        const filteredSites = customerSites.filter(s => (s.displayName ?? s.siteName ?? String(s.id)).toLowerCase().includes(siteSearchTerm.toLowerCase()));

                        return (
                            <div key={entry.customerId} className="csh-sites-section">
                                <div className="csh-sites-section__header">
                                    <span className="csh-sites-section__label">
                                        {customer.customerName} — Sites
                                        {!isLoading && customerSites.length > 0 && (
                                            <span className="csh-sites-section__count">
                                                ({entry.selectedSites.length} / {customerSites.length})
                                            </span>
                                        )}
                                    </span>

                                    <div className="csh-sites-section__controls">
                                        {!isLoading && customerSites.length > 4 && (
                                            <div className={`form__search-expandable ${siteSearchExpandedMap[entry.customerId] ? 'form__search-expandable--expanded' : ''}`}>
                                                <button
                                                    type="button"
                                                    className="form__search-icon-btn"
                                                    onClick={() => handleExpandSiteSearch(entry.customerId)}
                                                    aria-label={`Open ${customer.customerName} site search`}
                                                >
                                                    <span className="se-icon se-icon--search" />
                                                </button>
                                                <div className="form__search-input-wrapper">
                                                    <input
                                                        ref={(el) => {
                                                            if (el) siteSearchInputRefs.current[entry.customerId] = el;
                                                        }}
                                                        type="text"
                                                        className="form__control form__control--search"
                                                        placeholder={`Search sites...`}
                                                        value={siteSearchTerm}
                                                        onChange={(e) => handleSiteSearchChange(entry.customerId, e)}
                                                        onBlur={() => handleCollapseSiteSearch(entry.customerId)}
                                                        autoComplete="off"
                                                    />
                                                    {siteSearchTerm && (
                                                        <button
                                                            type="button"
                                                            className="form__search-clear"
                                                            onClick={() => handleClearSiteSearch(entry.customerId)}
                                                            aria-label={`Clear ${customer.customerName} site search`}
                                                        >
                                                            <span className="se-icon se-icon--close" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {!isLoading && customerSites.length > 1 && (
                                            <label className="csh-select-all-label">
                                                <input
                                                    type="checkbox"
                                                    className="csh-select-all-checkbox"
                                                    checked={customerSites.length > 0 && customerSites.every(s => entry.selectedSites.includes(String(s.id)))}
                                                    onChange={() => handleToggleAllSites(entry.customerId, customerSites)}
                                                    aria-label={`Select all sites for ${customer.customerName}`}
                                                />
                                                <span className="csh-select-all-text">Select All</span>
                                            </label>
                                        )}
                                    </div>
                                </div>

                                {isLoading ? (
                                    <div className="csh-sites-loader" role="status" aria-live="polite">
                                        <span className="csh-sites-loader__spinner" aria-hidden="true" />
                                        <span className="csh-sites-loader__text">Loading...</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="csh-bubble-list">
                                            {filteredSites.map(s => {
                                                const sId = String(s.id);
                                                const isSelected = entry.selectedSites.includes(sId);
                                                return (
                                                    <button
                                                        key={sId}
                                                        type="button"
                                                        className={`csh-bubble csh-bubble--site ${isSelected ? "csh-bubble--selected" : ""}`}
                                                        onClick={() => handleToggleSite(entry.customerId, sId)}
                                                    >
                                                        {s.displayName ?? s.siteName ?? sId}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {filteredSites.length === 0 && (
                                            <div className="csh-no-results">
                                                <span className="csh-no-results__text">
                                                    {siteSearchTerm ? "No matching sites found" : "No sites available"}
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};